// ⌘K command palette — Linear-style global jump.
//
// Mounts once at the AuthedLayout level. Cmd/Ctrl+K (or `/` outside an
// input) opens it. Searches across:
//   * Pages       — static list (Tickets, Reports, KB, …)
//   * Contacts    — fuzzy match across email + name
//   * Tickets     — the same server search as the Tickets page (this inbox,
//                   every status, partial words, ids, notes, To/CC)
//   * KB          — fuzzy match across title + tags
//   * Actions     — context-sensitive (e.g. "Resolve this ticket"
//                   when the user is on a ticket detail page)
//
// Architecture choices:
//   * Data is fetched once when the palette opens (not pre-loaded), to keep
//     idle render cost zero. Subsequent opens use the React Query cache so
//     they feel instant.
//   * Filtering is plain string-includes — at our volume (hundreds of
//     tickets, dozens of contacts) this is more than fast enough and
//     avoids pulling in a fuzzy-match library.
//   * Keyboard nav: ↑/↓ to move selection, Enter to activate, Esc to
//     close. We intentionally don't capture Tab — let it fall through to
//     focus management.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Activity as ActivityIcon,
  AlertOctagon,
  Archive,
  ArchiveRestore,
  ArrowRight,
  AlarmClock,
  Ban,
  Bell,
  BookOpen,
  Bot,
  CheckCircle2,
  Clock,
  Flag,
  Hand,
  RotateCcw,
  Home,
  Inbox,
  LayoutDashboard,
  Mail,
  Search,
  Settings as SettingsIcon,
  Sparkles,
  Tag,
  User as UserIcon,
  Users as UsersIcon,
  type LucideIcon,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useContactSearch,
  useKbArticles,
  usePaletteTicketSearch,
  useRecentInboxTickets,
  useTeamMembers,
  useTicket,
} from '@/lib/queries';
import { useAuth } from '@/lib/auth-guard';
import { useTicketAction } from '@/lib/use-ticket-action';
import { filterActions, ticketActionsFor } from '@/lib/palette-actions';
import { loadListContext, neighbours } from '@/lib/ticket-nav';
import { sessionStore } from '@/lib/browser-storage';
import { advanceFrom } from '@/lib/use-ticket-nav';
import { useTicketReads } from '@/lib/use-unread';
import { useSetReminder } from '@/lib/use-reminders';
import { describeWhen, reminderPresets } from '@/lib/reminders';
import { toastSuccess } from '@/lib/toast';
import { highlightTermsOf, parseSearchQuery, type SearchMatchSource } from '@/lib/search-query';
import { parseInboxPath, readLastVisited } from '@/lib/inbox-scope';
import { useAccess, useViewableInboxes } from '@/lib/use-access';
import { cn } from '@/lib/utils';

type CommandKind = 'page' | 'ticket' | 'contact' | 'kb' | 'action';

// Where a ticket hit came from, when that is not obvious from the subject.
const MATCH_VIA: Record<SearchMatchSource, string | null> = {
  id: 'ticket ID',
  subject: null,
  sender: null,
  contact: 'contact',
  note: 'internal note',
  message: 'in a message',
  recipient: 'To / CC / attachment',
  summary: 'summary',
};

interface PaletteCommand {
  kind: CommandKind;
  id: string;
  label: string;
  hint?: string;
  icon: LucideIcon;
  // What happens on Enter / click. Returning false keeps the palette open.
  perform: () => void;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Labels here mirror the nav exactly (see admin-ui/src/components/TopNav.tsx
// NAV_ENTRIES) so a user can search the palette using the same word they
// see in the bar. Routes from all three top-nav groups are included so
// nothing is reachable only via the dropdown.
// `perm` is the workspace permission that shows the entry (same as the nav).
// Icon per ticket action, for the actions you can type on a ticket page.
function iconForAction(action: string): LucideIcon {
  if (action === 'resolve') return CheckCircle2;
  if (action === 'close') return Ban;
  if (action === 'reopen') return RotateCcw;
  if (action === 'claim' || action === 'reassign') return Hand;
  if (action === 'unsnooze') return Bell;
  if (action.startsWith('snooze')) return Clock;
  if (action === 'escalate') return AlertOctagon;
  if (action === 'set_priority') return Flag;
  if (action === 'archive') return Archive;
  if (action === 'unarchive') return ArchiveRestore;
  return ArrowRight;
}

const PAGES: Array<{ to: string; label: string; icon: LucideIcon; perm?: string }> = [
  // Daily flow
  { to: '/my-day',       label: 'My Day',             icon: Home },
  { to: '/dashboard',    label: 'Tickets',            icon: Inbox },
  { to: '/dashboard?group=fyi', label: 'FYI (automated mail)', icon: Bot },
  { to: '/activity',     label: 'Activity',           icon: ActivityIcon },
  { to: '/contacts',     label: 'Contacts',           icon: UsersIcon },
  // Library
  { to: '/kb',           label: 'Knowledge base',     icon: BookOpen },
  { to: '/templates',    label: 'Reply templates',    icon: Tag },
  // Insights
  { to: '/reports',      label: 'Reports',            icon: LayoutDashboard },
  { to: '/journeys',     label: 'Client journeys',    icon: ArrowRight },
  // Admin
  { to: '/team',         label: 'Team members',       icon: UsersIcon,    perm: 'team.view' },
  { to: '/roles',        label: 'Roles',              icon: UsersIcon,    perm: 'team.view' },
  { to: '/access',       label: 'Who can do what',    icon: UsersIcon,    perm: 'team.view' },
  { to: '/teams',        label: 'Groups',             icon: UsersIcon,    perm: 'team.view' },
  { to: '/sla-rules',    label: 'SLA rules',          icon: SettingsIcon, perm: 'sla.manage' },
  { to: '/leave',        label: 'Leave requests',     icon: SettingsIcon, perm: 'leave.approve' },
  { to: '/inboxes',      label: 'Inboxes',            icon: Inbox,        perm: 'inboxes.manage' },
  { to: '/automations',  label: 'Automations',        icon: SettingsIcon, perm: 'automations.manage' },
  { to: '/company',      label: 'Company branding',   icon: SettingsIcon, perm: 'company.manage' },
  // Personal
  { to: '/settings',     label: 'Settings & signature', icon: SettingsIcon },
  { to: '/notifications', label: 'Notifications',      icon: Mail },
  { to: '/changelog',    label: "What's new",          icon: Sparkles },
];

export function CommandPalette({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // The palette is mounted above InboxScopeProvider (global), so it can't
  // useCurrentInbox(). Mirror InboxSwitcher: read the slug from the URL,
  // falling back to the last-visited inbox, so a contact link is scoped to
  // /inbox/<slug>/ like the contacts page. A stale/invalid slug is safe —
  // InboxScopeProvider redirects to the default inbox and DefaultInboxRedirect
  // preserves the ?email= query param.
  const inboxSlug = parseInboxPath(pathname).slug ?? readLastVisited();
  const contactHref = (email: string) => {
    const qs = '?email=' + encodeURIComponent(email);
    return inboxSlug ? `/inbox/${inboxSlug}/contacts/view${qs}` : `/contacts/view${qs}`;
  };
  // Ticket search is per inbox (same rule as the Tickets page): the one in the
  // URL, else the last visited, else the primary.
  const access = useAccess();
  const inboxesQ = useViewableInboxes();
  const inboxId = useMemo(() => {
    const active = (inboxesQ.data ?? []).filter((i) => i.active);
    const match =
      active.find((i) => i.label_namespace === inboxSlug) ??
      active.find((i) => i.is_primary) ??
      active[0];
    return match?.id;
  }, [inboxesQ.data, inboxSlug]);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // On a ticket page the palette can also DO things: type "resolve", "snooze",
  // "assign liz", "p1", "next" ... (lib/palette-actions.ts decides which fit).
  const ticketIdOnPage = /^\/tickets\/([^/?#]+)/.exec(pathname)?.[1];
  const pageTicketQ = useTicket(open ? ticketIdOnPage : undefined);
  const membersQ = useTeamMembers();
  const { teamMember } = useAuth();
  const { run } = useTicketAction();
  const { markUnread } = useTicketReads();
  const setReminder = useSetReminder();
  const pageNb = useMemo(
    () => (open ? neighbours(loadListContext(sessionStore()), ticketIdOnPage) : null),
    [open, ticketIdOnPage],
  );

  // Data. Tickets come from the server search (debounced, 2+ characters);
  // with an empty box the newest few tickets show instead. Both are React Query
  // hooks, so reopening is cache-instant.
  const ticketSearchQ = usePaletteTicketSearch(inboxId, query);
  const recentTicketsQ = useRecentInboxTickets(inboxId, 5);
  // Contacts are searched on the server as you type (RLS limits them to senders
  // you can see) instead of filtering a 200-row list in the browser.
  const contactsQ = useContactSearch(query);
  const kbQ = useKbArticles();

  // Reset query on open and focus the input. This is a separate effect
  // from open-state because we want focus to fire AFTER the dialog has
  // rendered (else the input ref isn't attached).
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      // Microtask delay so the focus runs after the Radix Dialog
      // has settled focus traps.
      const t = window.setTimeout(() => inputRef.current?.focus(), 30);
      return () => window.clearTimeout(t);
    }
  }, [open]);

  // Build the unified command list, filtered by the current query.
  const commands = useMemo<PaletteCommand[]>(() => {
    const q = query.trim().toLowerCase();
    const out: PaletteCommand[] = [];

    // Pages always shown. Filter by query when one's typed.
    for (const p of PAGES) {
      if (p.perm && !access.can(p.perm)) continue;
      if (q && !p.label.toLowerCase().includes(q)) continue;
      out.push({
        kind: 'page',
        id: 'page:' + p.to,
        label: p.label,
        hint: p.to,
        icon: p.icon,
        perform: () => {
          navigate(p.to);
          onOpenChange(false);
        },
      });
    }

    // Ticket actions: only on a ticket page, and only once something is typed.
    const pt = pageTicketQ.data;
    if (pt && ticketIdOnPage && q) {
      const acts = filterActions(
        ticketActionsFor({
          ticket: pt,
          mySlackId: teamMember?.slack_user_id ?? null,
          members: membersQ.data ?? [],
          can: (perm) => access.canIn(pt.inbox_id, perm),
          hasList: !!pageNb,
          hasNext: !!pageNb?.next,
          hasPrev: !!pageNb?.prev,
        }),
        query,
      );
      for (const a of acts) {
        const intent = a.intent;
        out.push({
          kind: 'action',
          id: 'action:' + a.id,
          label: a.label,
          hint: 'This ticket',
          icon:
            intent.type === 'nav'
              ? ArrowRight
              : intent.type === 'mark-unread'
                ? Mail
                : intent.type === 'remind'
                  ? AlarmClock
                  : iconForAction(intent.action),
          perform: () => {
            onOpenChange(false);
            if (intent.type === 'remind') {
              const when = reminderPresets(new Date()).find((p) => p.key === intent.preset)?.at;
              if (!when) return;
              setReminder.mutate(
                { ticketId: pt.id, remindAt: when, onlyIfNoReply: true, note: '' },
                {
                  onSuccess: () =>
                    toastSuccess('Reminder set', `You'll hear about this ${describeWhen(when)} if the client hasn't replied.`),
                },
              );
              return;
            }
            if (intent.type === 'mark-unread') {
              void markUnread(pt.id);
              return;
            }
            if (intent.type === 'nav') {
              const to = intent.to === 'next' ? pageNb?.next : pageNb?.prev;
              if (to) navigate('/tickets/' + to);
              return;
            }
            void run(pt.id, intent.action as Parameters<typeof run>[1], intent.payload, { subject: pt.subject });
            if (intent.advance) advanceFrom(pt.id, navigate);
          },
        });
      }
    }

    // Tickets: server search when something is typed, newest five otherwise.
    const typed = q.length >= 2;
    const findTerm = typed ? highlightTermsOf(parseSearchQuery(query))[0] : undefined;
    const ticketItems = typed
      ? (ticketSearchQ.data?.rows ?? []).map((r) => ({
          id: r.ticket.id,
          subject: r.ticket.subject,
          sender_email: r.ticket.sender_email,
          priority: r.ticket.priority,
          status: r.ticket.status,
          via: r.match ? MATCH_VIA[r.match.source] : null,
          messageId: r.match?.source === 'message' ? r.match.message_id : undefined,
        }))
      : (recentTicketsQ.data ?? []).map((t) => ({
          id: t.id,
          subject: t.subject,
          sender_email: t.sender_email,
          priority: t.priority,
          status: t.status,
          via: null as string | null,
          messageId: undefined as string | undefined,
        }));
    for (const t of ticketItems) {
      out.push({
        kind: 'ticket',
        id: 'ticket:' + t.id,
        label: t.subject || '(no subject)',
        hint:
          (t.priority ? `${t.priority} · ` : '') +
          (t.sender_email ?? '') +
          (t.status ? ` · ${t.status}` : '') +
          (t.via ? ` · ${t.via}` : ''),
        icon: Inbox,
        perform: () => {
          const qs = new URLSearchParams();
          if (findTerm) qs.set('find', findTerm);
          if (t.messageId) qs.set('m', t.messageId);
          const suffix = qs.toString();
          navigate('/tickets/' + t.id + (suffix ? '?' + suffix : ''));
          onOpenChange(false);
        },
      });
    }

    // Contacts — only when the user typed (otherwise the palette gets noisy).
    if (q) {
      for (const c of (contactsQ.data ?? []).slice(0, 8)) {
        out.push({
          kind: 'contact',
          id: 'contact:' + c.email,
          label: c.full_name || c.email,
          hint: c.email,
          icon: UserIcon,
          perform: () => {
            navigate(contactHref(c.email));
            onOpenChange(false);
          },
        });
      }
    }

    // KB — only when typing.
    if (q) {
      const articles = kbQ.data ?? [];
      const matchedKb = articles.filter(
        (a) =>
          (a.title ?? '').toLowerCase().includes(q) ||
          (a.content ?? '').toLowerCase().includes(q) ||
          (a.tags ?? []).some((t) => t.toLowerCase().includes(q)),
      );
      for (const a of matchedKb.slice(0, 6)) {
        out.push({
          kind: 'kb',
          id: 'kb:' + a.id,
          label: a.title,
          hint: (a.tags ?? []).join(', '),
          icon: BookOpen,
          perform: () => {
            // Opens that article (scrolled to and highlighted), not just the KB page.
            navigate('/kb?article=' + encodeURIComponent(a.id));
            onOpenChange(false);
          },
        });
      }
    }

    return out;
  }, [
    query,
    ticketSearchQ.data,
    recentTicketsQ.data,
    contactsQ.data,
    kbQ.data,
    navigate,
    onOpenChange,
    access,
    pageTicketQ.data,
    ticketIdOnPage,
    teamMember,
    membersQ.data,
    pageNb,
    run,
    markUnread,
    setReminder,
  ]);

  // Clamp activeIndex when the list shrinks (e.g. user types more).
  useEffect(() => {
    if (activeIndex >= commands.length) setActiveIndex(Math.max(0, commands.length - 1));
  }, [commands.length, activeIndex]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % Math.max(commands.length, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + Math.max(commands.length, 1)) % Math.max(commands.length, 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = commands[activeIndex];
      if (cmd) cmd.perform();
    }
  }

  // Group commands by kind for the section headers.
  const grouped = useMemo(() => {
    const order: CommandKind[] = ['action', 'page', 'ticket', 'contact', 'kb'];
    const map = new Map<CommandKind, PaletteCommand[]>();
    for (const c of commands) {
      const arr = map.get(c.kind) ?? [];
      arr.push(c);
      map.set(c.kind, arr);
    }
    return order
      .map((k) => ({ kind: k, items: map.get(k) ?? [] }))
      .filter((g) => g.items.length > 0);
  }, [commands]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="overflow-hidden p-0 sm:max-w-xl">
        {/* Radix requires a Title on every Dialog — without one it logs an
            error and the dialog is announced as unnamed. The palette is a
            primary surface, so it needs a real name even though the design
            has no visible heading. Same for the search input, whose only
            label was a placeholder (which vanishes the moment you type). */}
        <DialogTitle className="sr-only">Search and jump to</DialogTitle>
        <DialogDescription className="sr-only">
          Search tickets, contacts, knowledge-base articles and pages. Use the
          arrow keys to move through results and Enter to open one.
        </DialogDescription>
        <div className="flex items-center gap-2 border-b border-border/60 px-3 py-2.5">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              ticketIdOnPage
                ? 'Jump to a ticket or page, or type an action: resolve, snooze, assign…'
                : 'Jump to a ticket, contact, KB article, or page…'
            }
            aria-label="Search tickets, contacts, KB articles and pages"
            className="flex-1 border-0 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
          <kbd className="hidden h-5 items-center rounded border border-border/60 bg-muted px-1.5 text-3xs text-muted-foreground sm:inline-flex">
            esc
          </kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {grouped.length === 0 ? (
            <p className="px-4 py-12 text-center text-sm text-muted-foreground">
              {query.trim()
                ? ticketSearchQ.isFetching
                  ? 'Searching…'
                  : 'No matches.'
                : 'Start typing to search.'}
            </p>
          ) : (
            grouped.map(({ kind, items }) => (
              <div key={kind} className="py-1">
                <p className="px-3 py-1 text-3xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {sectionLabel(kind)}
                </p>
                <ul>
                  {items.map((cmd) => {
                    const overallIndex = commands.indexOf(cmd);
                    const active = overallIndex === activeIndex;
                    return (
                      <li key={cmd.id}>
                        <button
                          type="button"
                          onClick={() => cmd.perform()}
                          onMouseEnter={() => setActiveIndex(overallIndex)}
                          className={cn(
                            'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors',
                            active ? 'bg-accent text-foreground' : 'text-foreground/90 hover:bg-accent/40',
                          )}
                        >
                          <cmd.icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <span className="min-w-0 flex-1 truncate">{cmd.label}</span>
                          {cmd.hint ? (
                            <span className="hidden truncate text-xs text-muted-foreground sm:inline">
                              {cmd.hint}
                            </span>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-border/60 px-3 py-1.5 text-3xs text-muted-foreground">
          <span>
            <kbd className="mr-1 rounded border border-border/60 bg-muted px-1">↑</kbd>
            <kbd className="mr-1 rounded border border-border/60 bg-muted px-1">↓</kbd>
            navigate
          </span>
          <span>
            <kbd className="mr-1 rounded border border-border/60 bg-muted px-1">↵</kbd>
            open
          </span>
          <span>
            <kbd className="mr-1 rounded border border-border/60 bg-muted px-1">esc</kbd>
            close
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function sectionLabel(kind: CommandKind): string {
  switch (kind) {
    case 'action': return 'Actions';
    case 'page': return 'Pages';
    case 'ticket': return 'Tickets';
    case 'contact': return 'Contacts';
    case 'kb': return 'KB articles';
  }
}
